import type { WaitBeforeRemove } from './useWaitBeforeRemove';

import { campaignsClient } from 'clients/CampaignsClient/CampaignsClient';
import { queryClient } from 'config/queryClient';
import { usePageContext } from 'hooks/shared/usePageContext';
import { PageContext } from 'hooks/shared/usePageContext.types';
import { capitalize } from 'lodash';
import { useContext } from 'react';
import { CANDIDATE_STATUS, CandidateStatus } from 'services/candidates';
import { LocalizationContext } from 'shared/legacy/contexts/LocalizationContext/LocalizationContext';
import { CampaignTab } from 'shared/legacy/contexts/SelectedCampaignContext/SelectedCampaignContext.types';
import { SnackbarContext } from 'shared/legacy/contexts/SnackbarContext/SnackbarContext';
import { useCandidateStore } from 'stores/candidate/candidate.store';
import { QueryKey } from 'types/query';
import { calculateCandidateStatusFromLabels } from 'utils/candidates/calculateCandidateStatusFromLabels';
import { CandidateOrchestrator, WrappedHookParams, useCandidateAction } from './useCandidateAction';
import { isAReplyMessage } from './useContact.utils';
import { useSaveOnDb } from './useSaveOnDb';

export const useSaveLabels = (params: WrappedHookParams) => {
  const setCandidateLabels = useCandidateStore((s) => s.setCandidateLabels);
  const saveOnDb = useSaveOnDb(params.candidate);
  const pageContext = usePageContext();
  const { createSnackbar } = useContext(SnackbarContext);
  const { dictionary } = useContext(LocalizationContext);
  const isRatedCandidatesPage = pageContext === PageContext.RatedCandidates;

  return useCandidateAction<SaveLabelsHookCallbackParams, SaveLabelsCallbackParams>(
    async ({ candidate, labels }) => {
      await saveOnDb();

      const {
        data: { status: oldCandidateStatus },
      } = await campaignsClient.getCampaignCandidate({
        campaignId: String(candidate.campaignId),
        esPersonId: candidate.es_person_id,
      });

      const { data: { reasons = [] } = {} } = await campaignsClient.putCampaignCandidateLabels({
        labels,
        es_person_id: candidate.es_person_id,
        campaign_id: candidate.campaignId,
      });

      return { updatedLabels: reasons, oldCandidateStatus };
    },
    {
      onSuccess: ({ fcParams: { candidate, options: { waitBeforeRemoveFromList } = {} }, gtm }) => {
        const getNewStatus = (updatedLabels: string[]): CandidateStatus => {
          if (!candidate) {
            throw new Error('Candidate cannot be empty');
          }

          const { newStatus, addedLabels, removedLabels } = calculateCandidateStatusFromLabels({
            currentStatus: candidate.status,
            currentLabels: candidate.reasons,
            newLabels: updatedLabels,
          });

          addedLabels.forEach((label) => gtm.candidateLabelAdded(label, candidate.es_person_id));
          removedLabels.forEach((label) => gtm.candidateLabelRemoved(label));

          if (newStatus === CANDIDATE_STATUS.CONTACTED && candidate.status !== CANDIDATE_STATUS.CONTACTED) {
            const isReply = isAReplyMessage(candidate);
            gtm.contactCandidate(candidate.campaignId, candidate.es_person_id, pageContext, isReply);
            setTimeout(() => {
              queryClient.invalidateQueries([QueryKey.getInfiniteConversations], { exact: false });
            }, 5000);
          }

          return newStatus;
        };

        function updateStats({
          candidateStatus,
          newStatus,
          co,
        }: {
          candidateStatus: CandidateStatus;
          newStatus: CandidateStatus;
          co: ReturnType<CandidateOrchestrator>;
        }) {
          if (candidateStatus !== newStatus) {
            isRatedCandidatesPage ? co.ratedCandidates.remove.fromCurrentList() : co.campaign.remove.fromCurrentList();

            if (candidateStatus !== 'INVISIBLE') {
              isRatedCandidatesPage
                ? co.ratedCandidates.decreaseStatOf.list(candidateStatus as CampaignTab)
                : co.campaign.decreaseStatOf.list(candidateStatus as CampaignTab);
            }

            if (newStatus === CANDIDATE_STATUS.CONTACTED) {
              if (isRatedCandidatesPage) {
                co.ratedCandidates.increaseStatOf.contactList();
                co.ratedCandidates.reset.contactList();
              } else {
                co.campaign.increaseStatOf.contactList();
                co.campaign.reset.contactList();
              }
            }
            if (newStatus === CANDIDATE_STATUS.MATCHED) {
              if (isRatedCandidatesPage) {
                co.ratedCandidates.increaseStatOf.matchList();
                co.ratedCandidates.reset.matchList();
              } else {
                co.campaign.increaseStatOf.matchList();
                co.campaign.reset.matchList();
              }
            }

            if (newStatus === CANDIDATE_STATUS.HIDDEN) {
              if (isRatedCandidatesPage) {
                co.ratedCandidates.increaseStatOf.hiddenList();
                co.ratedCandidates.reset.hiddenList();
              } else {
                co.campaign.increaseStatOf.hiddenList();
                co.campaign.reset.hiddenList();
              }
            }
          }
        }

        return async ({ candidateOrchestrator: co, pageContext, data: { updatedLabels, oldCandidateStatus } }) => {
          const status = getNewStatus(updatedLabels);
          const removedLabels = candidate.reasons.filter((l) => !updatedLabels.includes(l));
          const campaignLabels = co.campaign.get.filterLabels();

          if (status !== CANDIDATE_STATUS.NEW && status !== CANDIDATE_STATUS.INVISIBLE) {
            createSnackbar(dictionary.candidateIsMovedTo(candidate.firstName, capitalize(status)), {
              variant: 'success',
            });
          }

          if (pageContext === PageContext.Campaign) {
            co.campaign.update.labels.set(updatedLabels);

            if (removedLabels.length > 0 && removedLabels.every((label) => campaignLabels.includes(label))) {
              co.campaign.remove.fromCurrentList();
              co.campaign.invalidateCurrentListInactiveQueries();
            }

            const removeFromCurrentList = () => {
              if (status !== CANDIDATE_STATUS.NEW || oldCandidateStatus === CANDIDATE_STATUS.NEW) {
                if (oldCandidateStatus === CANDIDATE_STATUS.NEW) {
                  co.campaign.decreaseStatOf.currentJobBoard();
                }

                updateStats({ candidateStatus: oldCandidateStatus, newStatus: status, co });
              }
            };

            if (waitBeforeRemoveFromList) {
              waitBeforeRemoveFromList().then(({ removeFromList }) => {
                if (removeFromList) {
                  removeFromCurrentList();
                }
              });
            } else {
              removeFromCurrentList();
            }
          } else if (pageContext === PageContext.RatedCandidates) {
            const campaignLabels = co.ratedCandidates.get.filterLabels();
            co.ratedCandidates.update.labels.set(updatedLabels);

            if (removedLabels.length > 0 && removedLabels.every((label) => campaignLabels.includes(label))) {
              co.ratedCandidates.remove.fromCurrentList();
              co.ratedCandidates.invalidateCurrentListInactiveQueries();
            }

            const removeFromCurrentList = () => {
              if (status !== CANDIDATE_STATUS.NEW || oldCandidateStatus === CANDIDATE_STATUS.NEW) {
                if (oldCandidateStatus === CANDIDATE_STATUS.NEW) {
                  co.ratedCandidates.decreaseStatOf.currentJobBoard();
                }
                updateStats({ candidateStatus: oldCandidateStatus, newStatus: status, co });
              }
            };

            if (waitBeforeRemoveFromList) {
              waitBeforeRemoveFromList().then(({ removeFromList }) => {
                if (removeFromList) {
                  removeFromCurrentList();
                }
              });
            } else {
              removeFromCurrentList();
            }
          } else if (pageContext === PageContext.Shared || pageContext === PageContext.Candidate) {
            co.share.update.locally({ reasons: updatedLabels, status });
          } else if (pageContext === PageContext.CentralInbox) {
            setCandidateLabels(updatedLabels);
          }
        };
      },
    },
  )(params);
};

type SaveLabelsCallbackParams = {
  updatedLabels: string[];
  oldCandidateStatus: CandidateStatus;
};

type SaveLabelsHookCallbackParams = {
  labels: string[];
  options?: {
    waitBeforeRemoveFromList?: WaitBeforeRemove;
  };
};
